// Parses the club's SPOT Drive naming conventions:
//   shared drive:  "SPOT - <year>"
//   event folder:  "<YYYYMMDD>_<slug>"
//   image file:    "<YYYYMMDD>_<HHMMSS>_<photographerCode>.<ext>"
//   noexport folder: "NOEXPORT_<name>" (also ".noexport-<name>", any case)

import {isDev} from "../env";

let DRIVE_YEAR_RE = /^SPOT - (\d{4})$/;
if (isDev()) {
    console.log('Using development drive year regex');
    DRIVE_YEAR_RE = /^SPOT - Teszt (\d{4})$/;
}

const EVENT_FOLDER_RE = /^(\d{4})(\d{2})(\d{2})_(.+)$/;
const PICTURE_FILE_NAME_RE = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_([a-zA-Z0-9_]+)(?:-([0-9]+))?\.([a-zA-Z0-9]+)$/;
const NOEXPORT_PREFIX_RE = /^\.?no[-_]?export[-_]/i;
const PICTURE_FILE_NAME_CHECK_RE = /^[0-9]{8}_[0-9]{6}_[a-zA-Z0-9_-]+\.(jpg|jpeg|JPG|JPEG)$/;
// A file extension: a short run of letters and digits, at least one of them a letter (so "v1.2" has none).
const FILE_EXTENSION_RE = /\.(?=[a-z0-9]*[a-z])[a-z0-9]{1,5}$/i;

// Unicode combining diacritical marks block (U+0300-U+036F), built from char codes to avoid
// source-encoding surprises with literal combining characters.
const DIACRITIC_MARKS_RE = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g');

export interface ParsedEventFolderName {
    date: Date;
    slug: string;
}

export interface ParsedFileName {
    date: Date;
    photographerCode: string;
    extension: string;
    suffixNumber: string | undefined;
}

function makeDate(year: string, month: string, day: string, hour = '0', minute = '0', second = '0'): Date {
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
}

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

export function parseDriveYear(driveName: string): number | null {
    const match = DRIVE_YEAR_RE.exec(driveName);
    return match ? Number(match[1]) : null;
}

export function formatDriveYearName(year: number): string {
    return `SPOT - ${year}`;
}

/** The prefix new noexport folders get. */
export const NOEXPORT_PREFIX = 'NOEXPORT_';

export interface ParsedFolderName {
    /** The name to show: without the noexport prefix. */
    displayName: string;
    noexport: boolean;
}

/** Splits off a folder name's noexport prefix ("noexport_", ".NOEXPORT-"…); a bare prefix is shown as is. */
export function parseFolderName(folderName: string): ParsedFolderName {
    const match = NOEXPORT_PREFIX_RE.exec(folderName);
    if (!match) return {displayName: folderName, noexport: false};
    return {displayName: folderName.slice(match[0].length) || folderName, noexport: true};
}

/** The name with the noexport prefix added, unless it already has one. */
export function withNoexportPrefix(folderName: string): string {
    return NOEXPORT_PREFIX_RE.test(folderName) ? folderName : NOEXPORT_PREFIX + folderName;
}

export function parseEventFolderName(folderName: string): ParsedEventFolderName | null {
    const match = EVENT_FOLDER_RE.exec(folderName);
    if (!match) return null;
    const [, year, month, day, slug] = match;
    return {date: makeDate(year, month, day), slug};
}

export function isPictureFileName(fileName: string): boolean {
    return PICTURE_FILE_NAME_CHECK_RE.test(fileName);
}

export function parseFileName(fileName: string): ParsedFileName | null {
    const match = PICTURE_FILE_NAME_RE.exec(fileName);
    if (!match) return null;
    const [, year, month, day, hour, minute, second, photographerCode, suffixNumber, extension] = match;
    return {
        date: makeDate(year, month, day, hour, minute, second),
        photographerCode,
        extension: extension.toLowerCase(),
        suffixNumber: suffixNumber ?? undefined,
    };
}

export const COPY_SUFFIX = ' - másolat';

/**
 * The default name of a file's copy: the suffix goes before the extension ("x.jpg" → "x - másolat.jpg"),
 * or at the end when the name has none — a dot that doesn't start an extension ("Dr. Kiss", "v1.2",
 * ".env") is just part of the name.
 */
export function defaultCopyName(fileName: string): string {
    const extension = FILE_EXTENSION_RE.exec(fileName);
    if (!extension || extension.index === 0) return fileName + COPY_SUFFIX;
    return fileName.slice(0, extension.index) + COPY_SUFFIX + extension[0];
}

/** The text without accents ("Gólyabál" → "Golyabal"). */
export function stripDiacritics(text: string): string {
    return text.normalize('NFD').replace(DIACRITIC_MARKS_RE, '');
}

/** Lowercases, strips diacritics, and replaces anything non-alphanumeric with underscores. */
export function slugify(title: string): string {
    const slug = stripDiacritics(title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return slug || 'event';
}

export function formatDisplayDate(date: Date): string {
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}.${m}.${d}.`;
}

export function formatDisplayDateTime(date: Date): string {
    const h = pad2(date.getHours());
    const min = pad2(date.getMinutes());
    const sec = pad2(date.getSeconds());
    return `${formatDisplayDate(date)} ${h}:${min}:${sec}`;
}

export function formatEventFolderName(date: Date, title: string): string {
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}${m}${d}_${slugify(title)}`;
}
